import React from 'react';
import { POS_GOLD } from '../../../../../config/posRoomStatus';
import { useConfig } from '../../../../../contexts/ConfigContext';
import { formatWeekdayDateLong, formatWeekdayDayMonth } from '../../../../../utils/formatters';
import CalendarFilterBar from './CalendarFilterBar';
import CalendarLegend from './CalendarLegend';
import { ReminderChips, useCalendarReminders } from './CalendarReminders';
import {
  PRIMARY_COLOR, STATUS_CONFIG, BLOCK_STYLE, TOTAL_CALENDAR_HOURS
} from './constants';
import {
  formatLocalDate, getInitials, getTerritoryCode,
  getTimePosition, getTimeHeight, getEventsForDate,
  calculateEventColumns, getAvailabilitySlots
} from './helpers';

interface CalendarFilters {
  search: string;
  status: string;
  territory_id: string;
  assigned_to: string;
  context: 'all' | 'pos' | 'cmb';
  show_blocks: boolean;
  show_reminders: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

interface CalendarBookingRow {
  id: string;
  booking_date_sast: string;
  start_time: string;
  end_time?: string;
  customer_name?: string;
  territory_name?: string;
  status: string;
  operator_names?: string[];
  booking_reference?: string;
  assigned_to_name?: string;
  is_pos?: boolean;
  [key: string]: unknown;
}

interface OperatorBlockRow {
  id: string;
  operator_id: string | null;
  reason?: string;
  operator_name?: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day?: boolean;
  [key: string]: unknown;
}

interface OperatorPatternRow {
  id: string;
  operator_id: string;
  days: string[];
  start_time: string;
  end_time: string;
  is_active: boolean;
  [key: string]: unknown;
}

interface DayViewProps {
  selectedDate: Date;
  isMobile: boolean;
  calendarFilters: CalendarFilters;
  setCalendarFilters: React.Dispatch<React.SetStateAction<CalendarFilters>>;
  calendarFiltersExpanded: boolean;
  setCalendarFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeCalendarFilterCount: number;
  statusOptions: FilterOption[];
  territoryOptions: FilterOption[];
  operatorOptions: FilterOption[];
  filteredCalendarBookings: CalendarBookingRow[];
  showBlocks: boolean;
  operatorBlocks: OperatorBlockRow[];
  operatorPatterns: OperatorPatternRow[];
  handleViewBooking: (booking: CalendarBookingRow) => void;
  handlePrevDay: () => void;
  handlePrevWeek: () => void;
  handleNextDay: () => void;
  handleNextWeek: () => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  handleToday: () => void;
  currentMonth: Date;
  viewMode: string;
  DAY_NAMES: readonly string[];
}

export default function DayView({
  selectedDate,
  isMobile,
  calendarFilters,
  setCalendarFilters,
  calendarFiltersExpanded,
  setCalendarFiltersExpanded,
  activeCalendarFilterCount,
  statusOptions,
  territoryOptions,
  operatorOptions,
  filteredCalendarBookings,
  showBlocks,
  operatorBlocks,
  operatorPatterns,
  handleViewBooking,
  handlePrevDay,
  handlePrevWeek,
  handleNextDay,
  handleNextWeek,
  handlePrevMonth,
  handleNextMonth,
  handleToday,
  currentMonth,
  viewMode,
  DAY_NAMES
}: DayViewProps) {
  const reminders = useCalendarReminders(calendarFilters);
  const { config } = useConfig();
  const hours = Array.from({ length: 11 }, (_, i) => i + 8);
  const dateStr = formatLocalDate(selectedDate, config?.timezone);
  // Compare on country-local date strings so a travelling super-admin sees
  // "today" in the country's clock, not their browser's.
  const todayStr = formatLocalDate(new Date(), config?.timezone);
  const isToday = dateStr === todayStr;
  const hourHeight = isMobile ? 60 : 80;

  const dayBookings = filteredCalendarBookings.filter(b => {
    if (!b.booking_date_sast) return false;
    return b.booking_date_sast === dateStr;
  });

  const dayEvents = getEventsForDate(dateStr, filteredCalendarBookings, showBlocks, operatorBlocks, calendarFilters);
  const eventsWithColumns = calculateEventColumns(dayEvents);

  return (
    <div className="card">
      <CalendarFilterBar
        calendarFilters={calendarFilters}
        setCalendarFilters={setCalendarFilters}
        calendarFiltersExpanded={calendarFiltersExpanded}
        setCalendarFiltersExpanded={setCalendarFiltersExpanded}
        activeCalendarFilterCount={activeCalendarFilterCount}
        statusOptions={statusOptions}
        territoryOptions={territoryOptions}
        operatorOptions={operatorOptions}
        filteredCalendarBookings={filteredCalendarBookings}
        showBlocks={showBlocks}
        viewMode={viewMode as 'day' | 'week' | 'month' | 'list'}
        isMobile={isMobile}
        handlePrevDay={handlePrevDay}
        handlePrevWeek={handlePrevWeek}
        handlePrevMonth={handlePrevMonth}
        handleNextDay={handleNextDay}
        handleNextWeek={handleNextWeek}
        handleNextMonth={handleNextMonth}
        handleToday={handleToday}
        selectedDate={selectedDate}
        currentMonth={currentMonth}
      />

      {/* Day and date, held at the top of the grid on every screen size. The nav
          strip carries its own date label but it is squeezed off on a phone, and
          stepping a day too far while checking availability is easy to do. */}
      <div className="cal-day-summary">
        <span className="fw-600">
          {isMobile ? formatWeekdayDayMonth(selectedDate) : formatWeekdayDateLong(selectedDate)}
        </span>
        <span className="flex gap-sm items-center">
          <span>{dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}</span>
          {isToday && <span className="fw-600 text-primary">Today</span>}
        </span>
      </div>

      {reminders.forDate(dateStr).length > 0 && (
        <div className="cal-day-reminders">
          <ReminderChips reminders={reminders.forDate(dateStr)} limit={8} />
        </div>
      )}

      <div className="cal-day-scroll">
        <div className="cal-time-grid">
          {/* Time labels */}
          <div className="cal-time-labels">
            {hours.map(hour => (
              <div key={hour} className="cal-time-label">
                {hour}:00
              </div>
            ))}
          </div>

          {/* Events column */}
          <div
            className="cal-events-col"
            style={{
              height: `${hours.length * hourHeight}px`,
              background: isToday ? `${PRIMARY_COLOR}04` : '#fff'
            }}
          >
            {/* Availability background */}
            {calendarFilters.assigned_to !== 'all' && getAvailabilitySlots(calendarFilters.assigned_to, selectedDate, operatorPatterns, DAY_NAMES).map((slot, slotIdx) => {
              const slotTop = getTimePosition(`${Math.floor(slot.startHour)}:${Math.round((slot.startHour % 1) * 60).toString().padStart(2, '0')}`);
              const slotHeight = ((slot.endHour - slot.startHour) / TOTAL_CALENDAR_HOURS) * 100;
              return (
                <div
                  key={`avail-${slotIdx}`}
                  className="cal-avail-slot"
                  style={{ top: `${slotTop}%`, height: `${slotHeight}%` }}
                  title="Available hours"
                />
              );
            })}

            {/* Hour grid lines */}
            {hours.map((hour, i) => (
              <div
                key={hour}
                className="cal-hour-line"
                style={{ top: `${i * hourHeight}px`, height: `${hourHeight}px` }}
              />
            ))}

            {/* Events */}
            {eventsWithColumns.map(event => {
              const top = getTimePosition(event.startTime);
              const height = getTimeHeight(event.startTime, event.endTime);
              const width = 100 / (event.totalColumns || 1);
              const left = (event.column || 0) * width;

              if (event.type === 'block') {
                const block = event.data as OperatorBlockRow;
                return (
                  <div
                    key={`block-${event.id}`}
                    className="cal-event-block"
                    style={{
                      top: `${top}%`,
                      height: `${Math.max(height, 8)}%`,
                      left: `calc(${left}% + 4px)`,
                      width: `calc(${width}% - 8px)`,
                      background: BLOCK_STYLE.bg,
                      borderLeft: `4px solid ${BLOCK_STYLE.border}`,
                      padding: 'var(--cal-event-pad, 8px)'
                    }}
                    title={`Blocked: ${block.operator_name} - ${block.reason || 'No reason'}`}
                  >
                    <div className="cal-event-badges mb-xs">
                      <span
                        className="cal-badge-initials"
                        style={{
                          background: BLOCK_STYLE.border,
                          color: 'var(--color-white)',
                          width: '24px',
                          height: '24px',
                          fontSize: 'var(--text-2xs)'
                        }}
                      >
                        {getInitials(block.operator_name)}
                      </span>
                      <span style={{ fontSize: 'var(--text-xs)', color: BLOCK_STYLE.color, fontWeight: 500 }}>
                        {block.is_all_day ? 'All Day' : `${block.start_time?.slice(0,5) || ''} - ${block.end_time?.slice(0,5) || ''}`}
                      </span>
                    </div>
                    <div className="cal-event-name" style={{ color: BLOCK_STYLE.color }}>
                      {block.reason || 'Blocked'}
                    </div>
                  </div>
                );
              }

              const booking = event.data as CalendarBookingRow;
              const status = (STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending)!;
              const territoryCode = getTerritoryCode(booking.territory_name);
              const operatorInitials = Array.isArray(booking.operator_names) && booking.operator_names.length > 0
                ? getInitials(booking.operator_names[0])
                : null;

              const brandColour = booking.is_pos ? POS_GOLD : 'var(--color-primary)';

              return (
                <div
                  key={`booking-${event.id}`}
                  className="cal-event"
                  onClick={() => handleViewBooking(booking)}
                  style={{
                    top: `${top}%`,
                    height: `${Math.max(height, 8)}%`,
                    left: `calc(${left}% + 4px)`,
                    width: `calc(${width}% - 8px)`,
                    background: status.bg,
                    borderLeft: `4px solid ${status.color}`,
                    padding: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  title={`${booking.booking_reference} - ${booking.customer_name}${booking.is_pos ? ' [POS]' : ''} (${booking.territory_name || 'Unknown'})`}
                >
                  {/* Brand banner — customer name on a violet (CMB) or gold (POS) strip */}
                  <div style={{
                    background: brandColour,
                    color: 'var(--color-white)',
                    padding: '4px 8px',
                    fontSize: 'var(--text-md)',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {booking.customer_name || 'No name'}
                    </span>
                    {booking.is_pos && (
                      <span style={{ background: 'rgba(255,255,255,0.22)', color: 'var(--color-white)', fontSize: 'var(--text-2xs)', fontWeight: 700, padding: '1px 5px', borderRadius: 3, letterSpacing: '0.04em' }}>POS</span>
                    )}
                  </div>
                  {/* Body */}
                  <div style={{ padding: 'var(--cal-event-pad, 8px)', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <div className="flex justify-between items-center mb-xs">
                      <span className="cal-event-time">
                        {booking.start_time}{booking.end_time ? ` - ${booking.end_time}` : ''}
                      </span>
                      <span className="cal-badge-status" style={{ background: status.color }}>
                        {status.label}
                      </span>
                    </div>
                    <div className="cal-event-badges">
                      {territoryCode && (
                        <span className="cal-badge-territory">
                          {territoryCode}
                          {!isMobile && height > 12 && (
                            <span style={{ fontWeight: 400, color: 'var(--color-text-light)' }}>
                              {booking.territory_name?.replace(/^CleanMyBed\s*/i, '')}
                            </span>
                          )}
                        </span>
                      )}
                      {operatorInitials && (
                        <span className="cal-badge-operator">
                          <span className="cal-badge-initials">
                            {operatorInitials}
                          </span>
                          {!isMobile && height > 12 && (
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>
                              {booking.assigned_to_name?.split(' ')[0]}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {eventsWithColumns.length === 0 && (
              <div className="cal-empty">No bookings scheduled for this day</div>
            )}
          </div>
        </div>
      </div>

      <CalendarLegend isMobile={isMobile} showBlocks={showBlocks} calendarFilters={calendarFilters} showReminders={reminders.on} />
    </div>
  );
}
